import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch } from "react-redux";
import { fetchCasesBySuiteId } from "../../../features/slices/Case";
import { AppDispatch } from "../../../store";
import ReportBrowser from "../../cases/components/ReportBrowser";

const tcColor = (
  v?: string
): "success" | "error" | "warning" | "info" | "default" => {
  const s = (v || "").toUpperCase();
  if (s === "PASSED") return "success";
  if (["FAILED", "INTERNAL ERROR", "ERROR"].includes(s)) return "error";
  if (["INTERRUPTED", "INIT"].includes(s)) return "warning";
  if (["RESERVED"].includes(s)) return "info";
  return "default";
};

const SuiteReportViewer: React.FC<{ suiteId?: number }> = ({ suiteId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | false>(false);

  useEffect(() => {
    if (suiteId === undefined) return;
    setLoading(true);
    dispatch(fetchCasesBySuiteId(suiteId))
      .unwrap()
      .then((res: any) => {
        const items = Array.isArray(res) ? res : res?.items || res?.cases || [];
        setCases(items);
      })
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, [suiteId, dispatch]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cases.length) {
    return (
      <Alert severity="info" sx={{ m: 1 }}>
        This suite has no cases to browse yet.
      </Alert>
    );
  }

  const withReport = cases.filter((c) => c.report_exists).length;

  return (
    <Box>
      <Typography color="text.secondary" sx={{ fontSize: "0.85rem", mb: 2 }}>
        Browse the report bundle this suite produces — {withReport} of{" "}
        {cases.length} cases have a stored report. Expand a case to inspect its
        files.
      </Typography>

      {cases.map((c) => (
        <Accordion
          key={c.id}
          disableGutters
          expanded={expanded === c.id}
          onChange={(_, isExp) => setExpanded(isExp ? c.id : false)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            mb: 1,
            "&:before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ width: "100%", pr: 1 }}
            >
              <Chip size="small" label={`#${c.id}`} variant="outlined" />
              <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.88rem" }}>
                {c.title || c.file_name || `Case ${c.id}`}
              </Typography>
              <Chip
                size="small"
                label={c.tc_result || "—"}
                color={tcColor(c.tc_result)}
                sx={{ fontWeight: 700 }}
              />
              {!c.report_exists && (
                <Chip size="small" label="no report" variant="outlined" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: "background.level1" }}>
            {expanded === c.id && (
              <ReportBrowser
                caseId={Number(c.id)}
                reportExists={!!c.report_exists}
                autoLoad
                framed={false}
              />
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default SuiteReportViewer;
